/**
 * CyberSells Achievements Module
 * Handles achievement unlocking and tracking
 */

(function() {
  'use strict';

  /**
   * Achievement definitions
   */
  const ACHIEVEMENTS = [
    // BEGINNER ACHIEVEMENTS
    {
      id: 'first_steps',
      title: 'First Steps',
      description: 'Create your CyberSells profile',
      icon: '??',
      xpReward: 50,
      condition: (playerData) => true // Auto-unlock
    },
    {
      id: 'shopper',
      title: 'Window Shopper',
      description: 'View 10 products',
      icon: '??',
      xpReward: 100,
      condition: (playerData) => (playerData.productsViewed?.length || 0) >= 10
    },
    {
      id: 'first_purchase',
      title: 'First Purchase',
      description: 'Complete your first order',
      icon: '??',
      xpReward: 200,
      condition: (playerData) => playerData.purchaseCount >= 1
    },

    // LEVEL ACHIEVEMENTS
    {
      id: 'level_5',
      title: 'Rising Star',
      description: 'Reach Level 5',
      icon: '?',
      xpReward: 300,
      condition: (playerData) => playerData.level >= 5
    },
    {
      id: 'level_10',
      title: 'Elite Player',
      description: 'Reach Level 10',
      icon: '??',
      xpReward: 500,
      condition: (playerData) => playerData.level >= 10
    },
    {
      id: 'level_15',
      title: 'Master Shopper',
      description: 'Reach Level 15',
      icon: '??',
      xpReward: 750,
      condition: (playerData) => playerData.level >= 15
    },

    // PURCHASE ACHIEVEMENTS
    {
      id: 'regular_customer',
      title: 'Regular Customer',
      description: 'Make 5 purchases',
      icon: '???',
      xpReward: 400,
      condition: (playerData) => playerData.purchaseCount >= 5
    },
    {
      id: 'loyal_customer',
      title: 'Loyal Customer',
      description: 'Make 10 purchases',
      icon: '??',
      xpReward: 600,
      condition: (playerData) => playerData.purchaseCount >= 10
    },
    {
      id: 'vip_shopper',
      title: 'VIP Shopper',
      description: 'Make 25 purchases',
      icon: '??',
      xpReward: 1000,
      condition: (playerData) => playerData.purchaseCount >= 25
    },

    // SPENDING ACHIEVEMENTS
    {
      id: 'big_spender_100',
      title: 'Big Spender',
      description: 'Spend $100 total',
      icon: '??',
      xpReward: 300,
      condition: (playerData) => (playerData.totalSpent || 0) >= 10000 // $100 in cents
    },
    {
      id: 'big_spender_500',
      title: 'High Roller',
      description: 'Spend $500 total',
      icon: '??',
      xpReward: 800,
      condition: (playerData) => (playerData.totalSpent || 0) >= 50000 // $500 in cents
    },
    {
      id: 'big_spender_1000',
      title: 'Whale Status',
      description: 'Spend $1000 total',
      icon: '??',
      xpReward: 1500,
      condition: (playerData) => (playerData.totalSpent || 0) >= 100000 // $1000 in cents
    },

    // STAT ACHIEVEMENTS
    {
      id: 'stat_master_50',
      title: 'Stat Collector',
      description: 'Earn 50 total stat points',
      icon: '??',
      xpReward: 400,
      condition: (playerData) => {
        if (!window.CyberSells_playerData) return false;
        return window.CyberSells_playerData.getTotalStatPoints(playerData) >= 50;
      }
    },
    {
      id: 'stat_master_100',
      title: 'Stat Master',
      description: 'Earn 100 total stat points',
      icon: '??',
      xpReward: 700,
      condition: (playerData) => {
        if (!window.CyberSells_playerData) return false;
        return window.CyberSells_playerData.getTotalStatPoints(playerData) >= 100;
      }
    },

    // SPECIAL ACHIEVEMENTS
    {
      id: 'cart_master',
      title: 'Cart Master',
      description: 'Add 20 items to cart',
      icon: '??',
      xpReward: 250,
      condition: (playerData) => (playerData.cartAdds || 0) >= 20
    },
    {
      id: 'explorer',
      title: 'Product Explorer',
      description: 'View 50 different products',
      icon: '??',
      xpReward: 500,
      condition: (playerData) => (playerData.productsViewed?.length || 0) >= 50
    },
    {
      id: 'equipment_collector',
      title: 'Equipment Collector',
      description: 'Purchase 10 equipment items',
      icon: '??',
      xpReward: 600,
      condition: (playerData) => {
        const equipmentPurchases = (playerData.itemsPurchased || []).filter(item => 
          item.stats && Object.keys(item.stats).length > 0
        );
        return equipmentPurchases.length >= 10;
      }
    },

    // ENGAGEMENT ACHIEVEMENTS
    {
      id: 'active_week',
      title: 'Active Week',
      description: 'Make a purchase 3 days in a row',
      icon: '??',
      xpReward: 400,
      condition: (playerData) => {
        // This would require more sophisticated date tracking
        // Simplified version: check if made purchases recently
        return playerData.purchaseCount >= 3;
      }
    },
    {
      id: 'quest_complete_10',
      title: 'Quest Hunter',
      description: 'Complete 10 quests',
      icon: '???',
      xpReward: 500,
      condition: (playerData) => {
        // Would need quest completion tracking
        return false; // Placeholder
      }
    }
  ];

  /**
   * Get all achievement definitions
   */
  function getAllAchievements() {
    return ACHIEVEMENTS;
  }

  /**
   * Get achievement by ID
   */
  function getAchievementById(id) {
    return ACHIEVEMENTS.find(a => a.id === id);
  }

  /**
   * Check which achievements should be unlocked
   * @returns {Array} Array of newly unlocked achievement IDs
   */
  function checkAchievements(playerData) {
    const currentAchievements = new Set(playerData.achievements || []);
    const newAchievements = [];

    ACHIEVEMENTS.forEach(achievement => {
      // Skip if already unlocked
      if (currentAchievements.has(achievement.id)) return;

      // Check if condition is met
      try {
        if (achievement.condition(playerData)) {
          newAchievements.push(achievement.id);
        }
      } catch (error) {
        if (window.cyberSellsConfig?.debug) {
          console.error(`[CyberSells] Error checking achievement ${achievement.id}:`, error);
        }
      }
    });

    return newAchievements;
  }

  /**
   * Award achievements to player
   * @returns {number} Total XP awarded from achievements
   */
  function awardAchievements(playerData, achievementIds) {
    if (!Array.isArray(achievementIds) || achievementIds.length === 0) {
      return 0;
    }

    let totalXp = 0;
    const achievements = playerData.achievements || [];

    achievementIds.forEach(id => {
      // Skip if already has achievement
      if (achievements.includes(id)) return;

      const achievement = getAchievementById(id);
      if (!achievement) return;

      // Add achievement
      achievements.push(id);
      totalXp += achievement.xpReward;

      if (window.cyberSellsConfig?.debug) {
        console.log('[CyberSells] Achievement unlocked:', achievement.title, `+${achievement.xpReward} XP`);
      }
    });

    playerData.achievements = achievements;
    playerData.achievementCount = achievements.length;

    return totalXp;
  }

  /**
   * Get achievement progress for display
   */
  function getAchievementProgress(playerData) {
    const unlocked = playerData.achievements?.length || 0;
    const total = ACHIEVEMENTS.length;
    const percentage = Math.floor((unlocked / total) * 100);

    return {
      unlocked,
      total,
      percentage
    };
  }

  /**
   * Get unlocked achievements with full data
   */
  function getUnlockedAchievements(playerData) {
    const unlockedIds = new Set(playerData.achievements || []);
    return ACHIEVEMENTS.filter(a => unlockedIds.has(a.id));
  }

  /**
   * Get locked achievements with full data
   */
  function getLockedAchievements(playerData) {
    const unlockedIds = new Set(playerData.achievements || []);
    return ACHIEVEMENTS.filter(a => !unlockedIds.has(a.id));
  }

  /**
   * Check and award achievements automatically
   */
  function checkAndAwardAchievements(playerData) {
    const newAchievements = checkAchievements(playerData);
    
    if (newAchievements.length === 0) {
      return { achievements: [], xpAwarded: 0 };
    }

    const xpAwarded = awardAchievements(playerData, newAchievements);
    
    // Get full achievement data
    const achievementData = newAchievements.map(id => getAchievementById(id)).filter(Boolean);

    // Show notifications
    if (window.cyberSells?._toastSystem) {
      achievementData.forEach((achievement, index) => {
        setTimeout(() => {
          window.cyberSells._toastSystem.showAchievement(achievement);
        }, index * 500);
      });
    }

    // Dispatch event
    document.dispatchEvent(new CustomEvent('cybersells:achievements-unlocked', {
      detail: { 
        achievements: achievementData,
        xpAwarded 
      }
    }));

    return {
      achievements: achievementData,
      xpAwarded
    };
  }

  // Expose module functions
  window.CyberSells_achievements = {
    getAllAchievements,
    getAchievementById,
    checkAchievements,
    awardAchievements,
    getAchievementProgress,
    getUnlockedAchievements,
    getLockedAchievements,
    checkAndAwardAchievements
  };

  // Auto-check achievements on player data updates
  document.addEventListener('cybersells:player-updated', (e) => {
    if (e.detail?.playerData) {
      checkAndAwardAchievements(e.detail.playerData);
    }
  });

})();
